import { Component, OnInit, ViewChildren, QueryList, AfterViewInit, OnDestroy } from '@angular/core';
import { timer } from 'rxjs';
import { ABCisApi, ApiService } from 'src/app/handlers/apiHandler.service';
import { takeWhile } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FormControl } from '@angular/forms';
import { SelectItem } from 'primeng/api/selectitem';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('chart') chartComponents: QueryList<any>;

  globalMaximum = 0;
  countTimer: any;
  countResponse: CountResponse[];
  configuration: JobConfiguration[];
  alive = true;
  clientCardConfigurations: any[];
  totals = 0;

  colorList: string[] = [ '#9B51E0',
    '#00D75B', '#F2C94C', '#F24A4A', '#E0518D', '#FC994A', '#2D9CDB', '#2DDBBC','#960300',
    '#9BAE27', '#6400C0', '#FF8E8E', '#00814B', '#0016DC' ];

  colorMap: { [s: string]: string; } = {};
  colorIndex = 0;

  chartOptions: any;

  refreshRate = new FormControl((1 * 60 * 1000));
  refreshOptions: SelectItem[] = [
    {label: '15 Seconds', value: (15 * 1000)},
    {label: '30 Seconds', value: (30 * 1000)},
    {label: '1 Minute', value: (1 * 60 * 1000)},
    {label: '2 Minutes', value: (2 * 60 * 1000)},
    {label: '5 Minutes', value: (5 * 60 * 1000)}
  ];

  constructor(private apiHandler: ApiService) { }

  ngOnInit() {
    Chart.register(...registerables);
    Chart.unregister(ChartDataLabels);
    this.configuration = [];
    this.clientCardConfigurations = [];
    this.ConfigureTimer();
  }

  ConfigureTimer() {
    this.countTimer = timer(0, this.refreshRate.value).pipe(takeWhile(() => this.alive)).subscribe(() => {
      this.apiHandler.getABCisRequest<any>(ABCisApi.Support, 'monitor/ocr-process-counts').then((response) => {
        this.countResponse = response.body;
        this.setConfiguration();
      });
    });
  }

  changeRefreshRate() {
    if (this.countTimer) {
      this.countTimer.unsubscribe();
    }
    this.ConfigureTimer();
  }

  setConfiguration() {
    this.totals = 0;
    this.configuration = this.getConfigurationJobs();
    this.getDataAndGraphs();
  }

  getDataAndGraphs() {
    this.configureColorMap();

    this.globalMaximum = 0;
    this.configuration.forEach(config => {
      if (this.globalMaximum < config.count) {
        this.globalMaximum = config.count;
      }
    });

    // Round to the nearest multiple of 10, with a minimum of 10
    // If the maximum is 0, set it to 10 to avoid empty charts
    if (this.globalMaximum === 0) {
      this.globalMaximum = 10;
    } else {
      this.globalMaximum = Math.ceil(this.globalMaximum / 10) * 10;
    }

    this.setChartOptions(this.globalMaximum);

    this.configuration.forEach(config => {
      config.channelGraph = this.getChannelGraphs(config.channels);
    });

    this.clientCardConfigurations = this.getClientCardData();

    setTimeout(() => {
      this.updateCharts();
    }, 100);
  }

  ngAfterViewInit() {
    this.chartComponents.changes.subscribe(() => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.updateCharts();
      }, 0);
    });

    // Initial chart update with slightly longer delay
    setTimeout(() => {
      this.updateCharts();
    }, 0);
  }




  updateCharts() {
    if (this.chartComponents && this.chartComponents.length > 0) {
      this.chartComponents.forEach((chartComponent, index) => {
        if (chartComponent && chartComponent.chart) {
          chartComponent.chart.setActiveElements([]);
          chartComponent.chart.options = { ...this.chartOptions };
          if (this.configuration[index] && this.configuration[index].channelGraph) {
            chartComponent.chart.data = this.configuration[index].channelGraph;
          }
          chartComponent.chart.update('none');
          chartComponent.chart.render();
        }
      });
    }
  }

  onDataSelect(event) {
    console.log('Data selected:', event);
  }

  setChartOptions(maximum) {
    this.chartOptions = {
      indexAxis: 'y',
      maintainAspectRatio: false,
      aspectRatio: 1.5,
      responsive: true,
      animation: {
        duration: 0
      },
      scales: {
        x: {
          stacked: true,
          min: 0,
          max: maximum,
          ticks: {
            beginAtZero: true
          }
        },
        y: {
          stacked: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          animation: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 4,
          displayColors: true,
          padding: 8,
          caretPadding: 4,
          position: 'nearest',
          xAlign: 'center',
          yAlign: 'center',
          callbacks: {
            title: function(context) {
              return `Channel: ${context[0].label}`;
            },
            label: function(context) {
              const clientName = context.dataset.label;
              const count = context.raw;
              if (count > 0) {
                return `${clientName}: ${count}`;
              }
              return null;
            },
            footer: function(tooltipItems) {
              let sum = 0;
              tooltipItems.forEach(function(tooltipItem) {
                if (tooltipItem.raw > 0) {
                  sum += tooltipItem.raw;
                }
              });
              return `Total: ${sum}`;
            }
          },
          filter: function(tooltipItem) {
            return tooltipItem.raw > 0;
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false,
        axis: 'y'
      },
      hover: {
        animationDuration: 0,
        mode: 'index',
        intersect: false,
        axis: 'y'
      },
      elements: {
        bar: {
          borderWidth: 0
        }
      }
    };
  }

  getClientCardData(): any[] {
    const ret: { [s: string]: any; } = {};

    this.configuration.forEach(config => {
      config.channels.forEach(channel => {
        channel.clients.forEach(clients => {
          if (clients.label != 'Pool') {
            if (ret[clients.label] !== undefined) {
              ret[clients.label].count += clients.count;
            } else {
              ret[clients.label] = {
                label: clients.label, count: clients.count, backgroundColor: this.colorMap[clients.label]
              };
            }
          }
        });
      });
    });

    return this.getDictionaryValues(ret);
  }

  configureColorMap() {
    this.colorMap['Pool'] = '#385072';
    this.colorMap['Document Manager'] = '#515FE0';

    for (let currentOCRClient = 1; currentOCRClient <= 14; currentOCRClient++) {
      this.configuration.forEach(config => {
        config.channels.forEach(channels => {
          channels.clients.forEach(client => {
            if (client.label.indexOf(`OCR ${currentOCRClient}`) !== -1 && this.colorMap[client.label] === undefined) {
              this.colorMap[client.label] = this.colorList[currentOCRClient - 1];
            }
          });
        });
      });
    }

    this.configuration.forEach(config => {
      config.channels.forEach(channels => {
        channels.clients.forEach(client => {
          if (this.colorMap[client.label] === undefined) {
            this.colorMap[client.label] = this.colorList[this.colorIndex];
            this.colorIndex++;
            if (this.colorIndex >= this.colorList.length) {
              this.colorIndex = 0; // Reset if we run out of colors
            }
          }
        });
      });
    });
  }

  getConfigurationJobs(): JobConfiguration[] {
    const ret: { [s: string]: JobConfiguration; } = {};
    this.countResponse.forEach(element => {
      if (ret[element.job] !== undefined) {
        ret[element.job].count += element.count;
      } else {
        ret[element.job] = {label: element.job, count: element.count};
        ret[element.job].channels = this.getJobChannelCounts(element.job);
      }
      this.totals += element.count;
    });

    return this.getDictionaryValues<JobConfiguration>(ret);
  }

  getJobChannelCounts(jobname): ChannelConfiguration[] {
    const ret: { [s: string]: ChannelConfiguration; } = {};
    this.countResponse.forEach(element => {
      if (element.job == jobname) {
        if (ret[element.channel] !== undefined) {
          ret[element.channel].count += element.count;
        } else {
          ret[element.channel] = {label: element.channel, count: element.count};
          ret[element.channel].clients = this.getJobChannelClientCounts(element.job, element.channel);
        }
      }
    });
    return this.getDictionaryValues<ChannelConfiguration>(ret);
  }

  getJobChannelClientCounts(jobname: string, channelName: string): ClientConfiguration[] {
    const poolConfiguration: ClientConfiguration = {label: 'Pool', count: 0};
    const ret: { [s: string]: ClientConfiguration; } = {};
    this.countResponse.forEach(element => {
      if (element.job == jobname && element.channel == channelName) {
        if (element.client == undefined) {
          poolConfiguration.count = element.count;
        } else {
          if (ret[element.client] !== undefined) {
            ret[element.client].count += element.count;
          } else {
            ret[element.client] = {label: element.client, count: element.count};
          }
        }
      }
    });
    return [poolConfiguration, ...this.getDictionaryValues<ClientConfiguration>(ret)];
  }

  getDictionaryValues<T>(dictionary: { [s: string]: T; }): T[] {
    const ret: T[] = [];
    for (const key in dictionary) {
      ret.push(dictionary[key]);
    }
    return ret;
  }

  getChannelGraphs(channels: ChannelConfiguration[]): any {
    const _labels = [];
    const _data = [];

    channels.forEach(element => {
      _labels.push(element.label);
    });

    const allClients = new Set<string>();
    channels.forEach(channel => {
      channel.clients.forEach(client => {
        allClients.add(client.label);
      });
    });

    Array.from(allClients).forEach(clientLabel => {
      if (this.colorMap[clientLabel]) {
        const temp = {
          label: clientLabel,
          data: [],
          backgroundColor: this.colorMap[clientLabel],
          stack: 'Stack 0',
          borderWidth: 0
        };

        channels.forEach(channel => {
          const findCount = channel.clients.find(x => x.label === clientLabel);
          temp.data.push(findCount ? findCount.count : 0);
        });

        _data.push(temp);
      }
    });

    return {
      labels: _labels,
      datasets: _data
    };
  }

  ngOnDestroy() {
    this.alive = false;
    if (this.countTimer) {
      this.countTimer.unsubscribe();
    }
  }
}

class CountResponse {
  job: string;
  channel: string;
  client: string;
  count: number;
}

class JobConfiguration {
  label: string;
  count = 0;
  channels?: ChannelConfiguration[];
  channelGraph?: any;
}

class ChannelConfiguration {
  label: string;
  count = 0;
  clients?: ClientConfiguration[];
}

class ClientConfiguration {
  label: string;
  count = 0;
}
